import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { DateTime } from 'luxon'
import { buildDateTime, isInvalidDate } from 'lib/CustomLuxonUtils'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import {
  enterTimeViewRenderers,
  mergeEnterTimePickerSlotProps,
  ENTER_TIME_FIELD_FORMAT,
} from './enter-time-view'

const TimePickerIcon = props => <ScheduleIcon fill={0} {...props} />

const handleTimeChange = (event, onChange, date) => {
  if (!event) return

  if (isInvalidDate(event)) {
    return onChange(DateTime.invalid(event))
  }

  const eventDateTime = buildDateTime(date, event)
  const year = date?.get('year') || eventDateTime.get('year')
  const month = date?.get('month') || eventDateTime.get('month')
  const day = date?.get('day') || eventDateTime.get('day')
  const hour = eventDateTime.get('hour')
  const minute = eventDateTime.get('minute')
  const roundedMinutesTime = DateTime.local(
    year,
    month,
    day,
    hour,
    Math.floor(minute / 5) * 5,
  )

  onChange(roundedMinutesTime)
}

const PGATimePicker = ({
  id,
  name,
  error,
  defaultValue,
  date,
  helperText,
  minutesStep,
  horizontalAnchor,
  onChanged,
  ...rest
}) => {
  const { control, register } = useFormContext()

  minutesStep = minutesStep || 1
  minutesStep = Math.min(Math.max(minutesStep, 1), 60)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale="en-us">
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ onChange, value }) => {
          return (
            <DesktopTimePicker
              {...rest}
              id={id}
              innerRef={register}
              minutesStep={minutesStep}
              closeOnSelect={false}
              views={['hours']}
              format={rest.format || ENTER_TIME_FIELD_FORMAT}
              viewRenderers={enterTimeViewRenderers}
              slots={{
                openPickerIcon: TimePickerIcon,
                ...(rest.slots || {}),
              }}
              slotProps={mergeEnterTimePickerSlotProps({
                ...(rest.slotProps || {}),
                textField: {
                  fullWidth: true,
                  name,
                  error,
                  helperText,
                  variant: rest.slotProps?.textField?.variant || 'outlined',
                  ...(rest.slotProps?.textField || {}),
                },
                popper: {
                  placement:
                    horizontalAnchor === 'right'
                      ? 'bottom-end'
                      : 'bottom-start',
                  ...(rest.slotProps?.popper || {}),
                },
              })}
              value={
                value != null && typeof value.toJSDate === 'function'
                  ? value.toJSDate()
                  : null
              }
              onChange={e =>
                handleTimeChange(
                  e,
                  next => {
                    onChanged?.(next)
                    onChange(next)
                  },
                  date,
                )
              }
            />
          )
        }}
      />
    </LocalizationProvider>
  )
}

export default PGATimePicker
